import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import qs from "qs";

import { isSignInWithEmailLink, onAuthStateChanged, sendSignInLinkToEmail, signInWithEmailLink } from "firebase/auth";
import { auth, isAdmin } from "../../firebase.js";
import AuthLayout from "../../components/AuthLayout.js";
import Message from "../../components/Message.js";
import Button from "../../components/Button.js";
import { redeemLoginToken } from "../../loginLinks.js";

// Een verminkte parameter (bv. afgekapt door een mailclient) mag de pagina niet
// laten crashen: atob gooit dan een fout. Leeg terug = "e-mailadres ontbreekt".
function decodeBase64url(str) {
  try {
    return atob(str.replace(/-/g, "+").replace(/_/g, "/"));
  } catch (e) {
    return "";
  }
}

function encodeBase64url(str) {
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// Alleen interne, absolute paden toestaan als redirect-bestemming na inloggen.
// Voorkomt open-redirects naar externe of protocol-relatieve URL's.
function safeNext(raw) {
  if (!raw || typeof raw !== "string") return null;
  if (!raw.startsWith("/") || raw.startsWith("//")) return null;
  return raw;
}

// Firebase zet onze eigen parameters (loginEmail, next) niet top-level in de
// link maar genest in `continueUrl`. Lees `next` daarom ook daar uit.
function extractNext(query) {
  if (query.next) return query.next;
  if (!query.continueUrl) return null;
  try {
    return new URL(query.continueUrl).searchParams.get("next");
  } catch (e) {
    return null;
  }
}

function extractEmail() {
  const href = window.location.href;
  const params = new URLSearchParams(window.location.search);

  for (const key of ["loginEmail", "email"]) {
    const direct = params.get(key);
    if (direct) return decodeBase64url(direct);
  }

  const continueUrl = params.get("continueUrl");
  if (continueUrl) {
    try {
      const cp = new URL(continueUrl).searchParams;
      for (const key of ["loginEmail", "email"]) {
        const val = cp.get(key);
        if (val) return decodeBase64url(val);
      }
    } catch (e) {}
  }

  const match = href.match(/[?&](?:loginEmail|email)[=|%3D]([A-Za-z0-9_-]+)/i);
  if (match) return decodeBase64url(match[1]);

  return "";
}

// Verwijder gevoelige queryparameters (de eenmalige oobCode en het
// e-mailadres) uit de adresbalk zodra ze verwerkt zijn, zodat ze niet blijven
// hangen in de browsergeschiedenis, screenshots of de Referer-header.
function stripSensitiveQuery() {
  try {
    window.history.replaceState({}, document.title, window.location.pathname);
  } catch (e) {}
}

function Action() {
  const location = useLocation();
  const query = qs.parse(location.search, { ignoreQueryPrefix: true });
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  // Firebase herstelt een bestaande sessie asynchroon, dus auth.currentUser is
  // bij de eerste render nog leeg. We houden de restore bij zodat we pas een
  // beslissing nemen als de sessiestatus bekend is.
  const [session, setSession] = useState({ ready: false, user: auth.currentUser });
  const attempted = useRef(false);
  // Bestemming vastleggen vóórdat we de query strippen, zodat de redirect
  // blijft werken nadat de URL is opgeschoond.
  const target = useRef(safeNext(extractNext(query)) || "/");
  // Ook het e-mailadres vastleggen vóór het strippen: nodig om bij een
  // ongeldige link met één klik een nieuwe link te kunnen sturen.
  const [linkEmail, setLinkEmail] = useState(extractEmail);
  const [resend, setResend] = useState("idle");
  // Inlogknoppen in e-mails dragen een eigen, langlevend token (?t=...) in
  // plaats van een Firebase-code; zie functions/login-links. Vastleggen vóór
  // het strippen van de query.
  const loginToken = useRef(typeof query.t === "string" ? query.t : "");
  const isSignIn = query.mode === "signIn" || !!loginToken.current;

  useEffect(() => onAuthStateChanged(auth, (user) => setSession({ ready: true, user })), []);

  useEffect(() => {
    if (!isSignIn) return;
    if (attempted.current) return;
    // Wacht tot Firebase een bestaande sessie heeft hersteld voordat we beslissen,
    // zodat we een actieve admin-sessie kunnen herkennen vóór we de link inwisselen.
    if (!session.ready) return;

    // Is er al een admin ingelogd, gebruik dan die sessie in plaats van de
    // eenmalige link in te wisselen. Een admin heeft toegang tot alle
    // ambulanceweergaven, dus we willen de admin niet uitloggen door de link te
    // consumeren en te wisselen naar het gekoppelde account.
    if (session.user && !session.user.isAnonymous && isAdmin()) {
      attempted.current = true;
      stripSensitiveQuery();
      setStatus("success");
      return;
    }

    attempted.current = true;

    if (loginToken.current) {
      redeemLoginToken(loginToken.current)
        .then(async (result) => {
          // Het adres altijd bewaren: mislukt de login hierna toch, dan kan de
          // bezoeker met één klik een nieuwe link laten sturen (het token
          // is dan al uit de adresbalk gehaald).
          if (result.email) setLinkEmail(result.email);
          if (result.status === "ok") {
            await signInWithEmailLink(auth, result.email, result.link);
            setStatus("success");
            return;
          }
          setError(result.status === "expired" ? "Deze link is verlopen." : "Deze link is ongeldig.");
          setStatus("error");
        })
        .catch((err) => {
          console.error(err);
          setError("Er ging iets mis bij het inloggen. Probeer het opnieuw.");
          setStatus("error");
        })
        .finally(stripSensitiveQuery);
      return;
    }

    const fullUrl = window.location.href;
    if (!isSignInWithEmailLink(auth, fullUrl)) {
      setStatus("error");
      setError("Deze link is ongeldig of verlopen.");
      return;
    }

    const email = extractEmail();
    if (!email) {
      setStatus("error");
      setError("E-mailadres ontbreekt in de link.");
      return;
    }

    // Het e-mailadres uit de link wordt uitsluitend gebruikt om de Firebase
    // sign-in af te ronden — Firebase bindt de eenmalige code aan dít adres —
    // en nergens anders als vertrouwde identiteit hergebruikt.
    signInWithEmailLink(auth, email, fullUrl)
      .then(() => setStatus("success"))
      .catch((err) => {
        console.error(err);
        setStatus("error");
        if (err.code === "auth/invalid-action-code") {
          setError("Deze link is verlopen of al gebruikt.");
        } else {
          setError("Er ging iets mis bij het inloggen. Probeer het opnieuw.");
        }
      })
      .finally(stripSensitiveQuery);
  }, [isSignIn, session.ready]);

  // Een Firebase-link is maar enkele uren geldig en alleen de laatst
  // gegenereerde per adres werkt; een token uit een mail kan verlopen zijn.
  // Stuur dan met één klik een verse link naar hetzelfde adres, met dezelfde
  // bestemming.
  async function onResend() {
    setResend("sending");
    try {
      await sendSignInLinkToEmail(auth, linkEmail, {
        url: window.location.origin + "/action?loginEmail=" + encodeBase64url(linkEmail) + "&next=" + encodeURIComponent(target.current),
        handleCodeInApp: true,
      });
      setResend("sent");
    } catch (err) {
      console.error(err);
      setResend("error");
    }
  }

  if (query.mode === "resetPassword") {
    return <Navigate replace to={`/reset-password${location.search}`} />;
  }

  if (query.mode === "verifyEmail") {
    return <Navigate replace to={`/verify-email${location.search}`} />;
  }

  if (isSignIn) {
    // Alleen doorsturen als de verse link daadwerkelijk is ingewisseld. We
    // sturen bewust NIET door op een reeds bestaande sessie voordat de sign-in
    // klaar is: een nieuwe link moet kunnen afronden zodat het juiste account
    // wint. Anders zou een al ingelogd (mogelijk ander) account worden
    // doorgestuurd zonder dat de link is verwerkt.
    if (status === "success") {
      return <Navigate replace to={target.current} />;
    }

    if (status === "error" && session.ready) {
      // De eenmalige code kon niet worden ingewisseld (al gebruikt door de
      // eerste knop in dezelfde mail, vooraf 'geconsumeerd' door een
      // e-mailscanner, of verlopen). Is de bezoeker tóch ingelogd, stuur dan
      // door naar de bestemming in plaats van een doodlopende fout te tonen.
      if (session.user && !session.user.isAnonymous) {
        return <Navigate replace to={target.current} />;
      }
      return (
        <AuthLayout>
          <div>
            {resend === "sent" ? (
              <Message
                variant="info"
                text={`We hebben een nieuwe inloglink gestuurd naar ${linkEmail}. Klik op de link in die e-mail om in te loggen.`}
              />
            ) : (
              <Message text={error} />
            )}
            {resend === "error" && (
              <div className="mt-4">
                <Message text="Versturen van een nieuwe link is niet gelukt. Probeer het opnieuw of log in." />
              </div>
            )}
            {linkEmail && resend !== "sent" && (
              <Button
                onClick={onResend}
                pending={resend === "sending"}
                label="Stuur mij een nieuwe inloglink"
                loadingLabel="Versturen..."
                className="mt-4 w-full"
              />
            )}
            <Link
              to="/login"
              state={{ from: target.current }}
              className="mt-3 w-full inline-flex items-center justify-center min-h-button px-4 py-2 text-base font-medium rounded-sm border border-border text-text-secondary hover:bg-bg-sunken transition-colors duration-fast"
            >
              Op een andere manier inloggen
            </Link>
          </div>
        </AuthLayout>
      );
    }

    return (
      <AuthLayout>
        <div className="flex items-center gap-3">
          <div className="w-5 h-5 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-text-secondary">Een moment, u wordt ingelogd...</p>
        </div>
      </AuthLayout>
    );
  }

  return <Navigate replace to="/" />;
}

export default Action;
